"use client";

import { OnboardingItemLayout } from "@/components/Onboarding/components/OnboardingItemLayout";
import { Button } from "@/components/ui/button";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useOnboardingStep } from "@/context/OnboardingStepProvider/OnboardingStepProvider";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { OnboardingStep, type OnboardingStepProps } from "../constants";

export const DoQuestsInfo = ({ visible }: OnboardingStepProps) => {
  const { goToNextStep, isModal } = useOnboardingStep();

  const { track } = useOnboardingAnalytics();
  const { currentStep } = useOnboardingStep();
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (hasViewedRef.current || !visible) return;
    hasViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.DO_QUESTS_INFO,
        step: currentStep,
      },
    });
  }, [track, visible, currentStep]);

  const handleButtonClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.DO_QUESTS_INFO,
        step: currentStep,
        control: "next-step",
        controlType: ControlType.BUTTON,
      },
    });

    goToNextStep();
  };

  const handleLinkClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.DO_QUESTS_INFO,
        step: currentStep,
        control: "continue-to-immutable-play",
        controlType: ControlType.LINK,
      },
    });
  };

  const handleImageClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.DO_QUESTS_INFO,
        step: currentStep,
        control: "do-quests",
        controlType: ControlType.IMAGE,
      },
    });
  };

  return (
    <OnboardingItemLayout>
      <div className="text-center">
        <h1 className="imx-gradient font-bold text-sm tracking-wider">
          HOW IT WORKS
        </h1>

        <h2 className="font-bold text-3xl">Do Quests</h2>

        <p className="mt-2">
          Earn gems and points by completing <br />
          quests for each game.
        </p>
      </div>

      <Image
        src="/images/onboarding/do-quests-example.webp"
        alt="Do quests"
        width={352}
        height={280}
        className="w-full max-h-[37.5dvh] object-contain"
        onClick={handleImageClick}
        priority
      />

      <div className="flex flex-col items-center gap-y-4 w-full">
        <Button
          size="lg"
          className="w-full rounded-full py-4 text-base font-semibold relative"
          onClick={handleButtonClick}
        >
          Next step
          <ArrowRight className="absolute size-5 right-4" />
        </Button>

        {!isModal ? (
          <Link
            href="/quests"
            className="text-text-body-secondary underline"
            onClick={handleLinkClick}
          >
            Continue to Immutable Play
          </Link>
        ) : null}
      </div>
    </OnboardingItemLayout>
  );
};
